import React, { useEffect, useMemo } from "react";
import {
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import { FormikProps, withFormik } from "formik";
import { Button } from "@chakra-ui/button";
import { Box, HStack, VStack } from "@chakra-ui/layout";
import {
  DndContext,
  DragEndEvent,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { AdminApiClient } from "../api/adminApiClient";
import { CherryHubAdminApi } from "../api/adminApiModels";
import { TextField } from "../components/fields/TextField/TextField";
import { SwitchField } from "../components/fields/SwitchField/SwitchField";
import { isEmptyStr } from "../util/stringHelper";
import { FormStack } from "../components/FormStack/FormStack";
import { usePaginatedAdminApiRequest } from "../hooks/usePaginatedApiRequest";
import { ChakraReactSelectField } from "../components/fields/SelectField/ChakraReactSelectField";
import * as Yup from "yup";
import { PlusIcon } from "../styles/icons";
import { NewDigitalMembershipCardPassConfigModal } from "../components/Business/NewDigitalMembershipCardPassConfigModal";
import { BusinessPassConfigurationRuleCard } from "../components/Business/BusinessPassConfigurationRuleCard";
import {
  createEmptyPassConfigurationRule,
  passConfigurationRulesArrayToMap,
  passConfigurationRulesMapToArray,
} from "../components/Business/passConfigurationRules";
import { useAdminApiRequest } from "../hooks/useApiRequest";
import { AdminApiEnumConfigurations } from "../enums/AdminApiEnumConfigurations";
import { TextArea } from "../components/fields/TextArea/TextArea";
import { CopyText } from "../components/Utilities/CopyText";
import { MemberFilterQueryModal } from "../components/MemberFilterQuery/MemberFilterQueryModal";
import { BusinessCustomerRiskManagementConfigurationSection } from "../components/Business/BusinessCustomerRiskManagementConfigurationSection";
import { BusinessDigitalMembershipOffboardingSection } from "../components/Business/BusinessDigitalMembershipOffboardingSection";
import { useConfirmationDialog } from "../hooks/useConfirmationDialog";
import DateUtil from "../util/DateUtil";

const emptyStrToNull = (value: string | null): string | null => {
  if (!value || value.trim() === "") {
    return null;
  } else {
    return value;
  }
};

const isExpireAllDmcAfterDateInPast = (
  utcDateTimeLocal: string | null | undefined
): boolean => {
  const iso = DateUtil.convertFromUtcDateTimeLocal(utcDateTimeLocal);
  if (iso == null) {
    return false;
  }
  return new Date(iso).getTime() < Date.now();
};

interface EditDigitalMembershipConfiguration
  extends Omit<
    CherryHubAdminApi.DigitalMembershipConfiguration,
    "PassConfigurationRules"
  > {
  PassConfigurationRules?: CherryHubAdminApi.PassConfigurationRule[];
}

interface EditBusinessFormValues {
  displayName: string | null;
  enabled: boolean;
  licencedCherryHubProducts?:
    | CherryHubAdminApi.LicencedCherryHubProduct[]
    | null;
  smsSenderId: string | null;
  allowDigitalMembershipCardCreation: boolean | undefined;
  digitalMembershipCardPassConfigurationId: string | null;
  defaultApplePassTypeIdentifier: string | null;
  defaultApplePassTypeIdentifierForCoupons: string | null;
  digitalMembershipConfiguration?: EditDigitalMembershipConfiguration;
  customerRiskManagementConfiguration?:
    | CherryHubAdminApi.Business["CustomerRiskManagementConfiguration"]
    | null;
}

const hasValue = (value?: string | null): boolean =>
  !!value && value.trim() !== "";

const isValidUrl = (value?: string | null): boolean => {
  if (!hasValue(value)) {
    return true;
  }
  try {
    new URL(value!);
    return true;
  } catch {
    return false;
  }
};

const hasBndryConfigInput = (
  config?: CherryHubAdminApi.Business["CustomerRiskManagementConfiguration"]
): boolean => {
  const bndry = config?.BndryApiClientConfiguration;
  return (
    hasValue(bndry?.ApiBaseUrl) ||
    hasValue(bndry?.ClientId) ||
    bndry?.LogRequests === true
  );
};

const hasAu10TixConfigInput = (
  config?: CherryHubAdminApi.Business["CustomerRiskManagementConfiguration"]
): boolean => {
  const au10Tix = config?.Au10TixApiClientConfiguration;
  return (
    hasValue(au10Tix?.AuthApiBaseUrl) ||
    hasValue(au10Tix?.ClientId) ||
    hasValue(au10Tix?.KeyId) ||
    au10Tix?.LogRequests === true ||
    au10Tix?.UseAuthApiBaseUrlForAllRequests === true
  );
};

const buildCustomerRiskManagementConfiguration = (
  values: EditBusinessFormValues
):
  | CherryHubAdminApi.Business["CustomerRiskManagementConfiguration"]
  | undefined => {
  const config = values.customerRiskManagementConfiguration;
  const bndry = config?.BndryApiClientConfiguration;
  const au10Tix = config?.Au10TixApiClientConfiguration;

  const bndryConfig = hasValue(bndry?.ClientId)
    ? {
        ClientId: bndry!.ClientId!.trim(),
        ApiBaseUrl: emptyStrToNull(bndry?.ApiBaseUrl ?? null),
        LogRequests: bndry?.LogRequests ?? false,
      }
    : undefined;

  const au10TixConfig =
    hasValue(au10Tix?.ClientId) && hasValue(au10Tix?.KeyId)
      ? {
          ClientId: au10Tix!.ClientId!.trim(),
          KeyId: au10Tix!.KeyId!.trim(),
          AuthApiBaseUrl: emptyStrToNull(au10Tix?.AuthApiBaseUrl ?? null),
          LogRequests: au10Tix?.LogRequests ?? false,
          UseAuthApiBaseUrlForAllRequests:
            au10Tix?.UseAuthApiBaseUrlForAllRequests ?? false,
        }
      : undefined;

  if (!bndryConfig && !au10TixConfig) {
    return undefined;
  }

  return {
    ...(bndryConfig && { BndryApiClientConfiguration: bndryConfig }),
    ...(au10TixConfig && { Au10TixApiClientConfiguration: au10TixConfig }),
  };
};

interface EditBusinessFormProps {
  apiClient: AdminApiClient;
  business: CherryHubAdminApi.Business;
  isDisabled?: boolean;
  onCancel: () => void;
  onSuccess?: (business?: CherryHubAdminApi.Business) => void;
  onFailure?: (message?: string) => void;
}

interface SortablePassConfigurationRuleProps {
  id: string;
  children: (props: {
    listeners: ReturnType<typeof useSortable>["listeners"];
  }) => JSX.Element;
}

const SortablePassConfigurationRule = ({
  id,
  children,
}: SortablePassConfigurationRuleProps) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id,
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.7 : 1,
  };

  return (
    <Box ref={setNodeRef} style={style} w="100%" {...attributes}>
      {children({ listeners })}
    </Box>
  );
};

const EmptyPassConfigurationRules = ({
  onAdd,
}: {
  onAdd: () => void;
}): JSX.Element => {
  return (
    <VStack w="100%" p={4} spacing={2}>
      <i>Pass Configuration Rules not set</i>
      <Button
        size="sm"
        leftIcon={<PlusIcon />}
        onClick={onAdd}
        colorScheme="cherryButton"
      >
        Add Pass Configuration Rule
      </Button>
    </VStack>
  );
};

const InnerForm = ({
  isSubmitting,
  submitForm,
  isValid,
  dirty,
  onCancel,
  values,
  business,
  setFieldValue,
}: EditBusinessFormProps & FormikProps<EditBusinessFormValues>) => {
  const { data: productOptions, isLoading: productOptionsLoading } =
    useAdminApiRequest(
      (apiClient) =>
        apiClient.getEnumConfigurations(
          AdminApiEnumConfigurations.LicencedCherryHubProduct
        ),
      []
    );

  const {
    refresh,
    items: digitalMembershipCards,
    isLoading,
  } = usePaginatedAdminApiRequest(
    // Request the first page
    (apiClient) =>
      apiClient.getBusinessDigitalMembershipCardPassConfiguration(
        business.BusinessId!
      ),
    // Request subsequent pages
    (apiClient, _, nextUrl) =>
      apiClient.getBusinessDigitalMembershipCardPassConfiguration(nextUrl),
    []
  );

  const { confirmationElement, showConfirmationDialog } =
    useConfirmationDialog();

  const memberFilterQueryModal = useDisclosure();

  const newPassConfigModal = useDisclosure();

  const digitalMembershipCardOptions = useMemo(
    () =>
      digitalMembershipCards.map((cards) => ({
        label: cards.DisplayName ?? "",
        value: cards.Meta?.Id!.replace(`${business.BusinessId}.`, "") ?? "",
      })),
    [digitalMembershipCards]
  );

  const passConfigurationRules =
    values.digitalMembershipConfiguration?.PassConfigurationRules ?? [];

  const hasPassConfigRules = passConfigurationRules.length > 0;

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 5 },
    })
  );

  useEffect(() => {
    if (!dirty) {
      return;
    }

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
      return event.returnValue;
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [dirty]);

  const addPassConfigRule = () => {
    setFieldValue("digitalMembershipConfiguration.PassConfigurationRules", [
      ...passConfigurationRules,
      createEmptyPassConfigurationRule(),
    ]);
  };

  const removePassConfigRule = (index: number) => {
    setFieldValue(
      "digitalMembershipConfiguration.PassConfigurationRules",
      passConfigurationRules.filter((_, ruleIndex) => ruleIndex !== index)
    );
  };

  const movePassConfigRule = (
    sourceIndex: number,
    destinationIndex: number
  ) => {
    if (
      sourceIndex < 0 ||
      destinationIndex < 0 ||
      sourceIndex >= passConfigurationRules.length ||
      destinationIndex >= passConfigurationRules.length
    ) {
      return;
    }

    setFieldValue(
      "digitalMembershipConfiguration.PassConfigurationRules",
      arrayMove([...passConfigurationRules], sourceIndex, destinationIndex)
    );
  };

  const onDragEndPassConfigurationRule = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) {
      return;
    }

    movePassConfigRule(
      parseInt(active.id.toString()),
      parseInt(over.id.toString())
    );
  };

  const onCancelWithUnsavedConfirm = () => {
    if (dirty) {
      showConfirmationDialog(
        "Unsaved changes",
        "You may lose changes. Are you sure you want to leave?",
        "Leave"
      ).then(({ ok }) => {
        if (ok) {
          onCancel();
        }
      });
      return;
    }

    onCancel();
  };

  const passConfigRulesElement = useMemo(() => {
    const rules: React.ReactNode[] = [];

    if (hasPassConfigRules) {
      passConfigurationRules.forEach((rule, index) => {
        const title = rule.Description?.trim()
          ? rule.Description
          : "(No description)";
        rules.push(
          <SortablePassConfigurationRule id={index.toString()} key={index}>
            {({ listeners }) => (
              <BusinessPassConfigurationRuleCard
                ruleIndex={index}
                title={`#${index + 1} ${title}`}
                dragListeners={listeners as Record<string, unknown>}
                onRemove={() => removePassConfigRule(index)}
                digitalMembershipCardOptions={digitalMembershipCardOptions}
                isLoading={isLoading}
              />
            )}
          </SortablePassConfigurationRule>
        );
      });
    } else {
      rules.push(
        <EmptyPassConfigurationRules
          key="empty-pass-config-rules"
          onAdd={addPassConfigRule}
        />
      );
    }

    return (
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={onDragEndPassConfigurationRule}
      >
        <VStack w="100%" spacing={2}>
          <SortableContext
            items={passConfigurationRules.map((_, index) => index.toString())}
            strategy={verticalListSortingStrategy}
          >
            {rules}
          </SortableContext>
        </VStack>
      </DndContext>
    );
  }, [
    hasPassConfigRules,
    passConfigurationRules,
    sensors,
    isLoading,
    digitalMembershipCardOptions,
  ]);

  const onSuccessMemberFilterQuery = (query: string | undefined) => {
    setFieldValue(
      "digitalMembershipConfiguration.ActiveMemberFilterQuerystring",
      query
    );
    memberFilterQueryModal.onClose();
  };

  const memberFilterButtonElement = (
    <Box flexShrink={1}>
      <Button
        onClick={memberFilterQueryModal.onOpen}
        alignSelf="flex-end"
        colorScheme="cherryButton"
      >
        Filters
      </Button>
    </Box>
  );

  return (
    <>
      <Box width="100%" alignSelf="flex-start">
        <FormStack>
          {business && (
            <HStack w="100%">
              <Box flexGrow={1} pb={4}>
                <CopyText text={business.BusinessId!} hover adjustMargin>
                  <Text fontSize="md" color="cherryUi.600">
                    {business.Meta?.Id}
                  </Text>
                </CopyText>
                <Text fontSize="xl" color="cherryUi.600">
                  <b>{business?.DisplayName}</b>
                </Text>
              </Box>

              <Box>
                <Button
                  colorScheme="cherryButton"
                  type="submit"
                  isLoading={isSubmitting}
                  disabled={isSubmitting || !isValid}
                  onClick={submitForm}
                >
                  Save
                </Button>
              </Box>

              <Box>
                <Button
                  disabled={isSubmitting}
                  onClick={onCancelWithUnsavedConfirm}
                >
                  Cancel
                </Button>
              </Box>
            </HStack>
          )}

          <VStack
            w="100%"
            maxW="800px"
            alignItems="flex-start"
            spacing={4}
            pb={10}
          >
            <TextField horizontal label="Display Name" name="displayName" />

            <SwitchField horizontal label="Enabled" name="enabled" />

            <ChakraReactSelectField
              horizontal
              label="Licenced Products"
              name="licencedCherryHubProducts"
              isLoading={productOptionsLoading}
              options={
                productOptions?.map((provider) => {
                  return {
                    label: provider.Value!,
                    value: provider.Key!,
                  };
                }) || []
              }
              selectedOptionColor="blue"
              isMulti
            />
          </VStack>

          <Tabs variant="edit" w="100%" isLazy>
            <TabList>
              <HStack spacing={3}>
                <Tab>Settings</Tab>

                <Tab>Digital Membership Configuration</Tab>

                <Tab>Customer Risk Management Configuration</Tab>

                <Tab>Offboarding</Tab>
              </HStack>
            </TabList>

            <TabPanels>
              <TabPanel>
                <VStack w="100%" alignItems="flex-start" spacing={4}>
                  <TextArea
                    horizontal
                    valueProps={{ flex: 2 }}
                    label="Active Member Filter Query"
                    name="ActiveMemberFilterQuerystring"
                    value={
                      values.digitalMembershipConfiguration
                        ?.ActiveMemberFilterQuerystring ?? ""
                    }
                    isReadOnly
                    rightElement={memberFilterButtonElement}
                  />

                  <TextField
                    horizontal
                    valueProps={{ flex: 2 }}
                    label="SMS SenderId"
                    name="smsSenderId"
                    maxLength={11}
                  />

                  <SwitchField
                    horizontal
                    valueProps={{ flex: 2 }}
                    label="Allow Digital Membership Card Creation"
                    name="allowDigitalMembershipCardCreation"
                  />

                  <ChakraReactSelectField
                    horizontal
                    valueProps={{ flex: 2 }}
                    label="Digital Membership Card Pass Configuration Id"
                    name="digitalMembershipCardPassConfigurationId"
                    isLoading={isLoading}
                    allowEmpty="Please select.."
                    options={digitalMembershipCardOptions}
                    selectedOptionColor="blue"
                    rightElement={
                      <Box flexShrink={1}>
                        <Button
                          leftIcon={<PlusIcon />}
                          colorScheme="cherryButton"
                          isLoading={isSubmitting}
                          disabled={isSubmitting}
                          alignSelf="flex-end"
                          onClick={newPassConfigModal.onOpen}
                        >
                          New
                        </Button>
                      </Box>
                    }
                  />

                  <TextField
                    horizontal
                    valueProps={{ flex: 2 }}
                    label="Default Apple Pass Type Identifier"
                    name="defaultApplePassTypeIdentifier"
                  />

                  <TextField
                    horizontal
                    valueProps={{ flex: 2 }}
                    label="Default Apple Pass Type Identifier For Coupons"
                    name="defaultApplePassTypeIdentifierForCoupons"
                  />
                </VStack>
              </TabPanel>

              <TabPanel>
                <VStack w="100%" spacing={4}>
                  {hasPassConfigRules && (
                    <HStack w="100%" justifyContent="space-between">
                      <Text color="cherryUi.600" fontSize="sm">
                        Drag and drop to re-order rules
                      </Text>
                      <Button
                        leftIcon={<PlusIcon />}
                        onClick={addPassConfigRule}
                        colorScheme="cherryButton"
                        alignSelf="flex-end"
                        size="sm"
                      >
                        Add Pass Configuration Rule
                      </Button>
                    </HStack>
                  )}

                  {passConfigRulesElement}
                </VStack>
              </TabPanel>

              <TabPanel>
                <BusinessCustomerRiskManagementConfigurationSection />
              </TabPanel>

              <TabPanel>
                <BusinessDigitalMembershipOffboardingSection />
              </TabPanel>
            </TabPanels>
          </Tabs>

          <HStack width="100%" justifyContent="start" spacing="3" pt="8">
            <Button
              colorScheme="cherryButton"
              type="submit"
              isLoading={isSubmitting}
              disabled={isSubmitting || !isValid}
              onClick={submitForm}
            >
              Save
            </Button>
            <Button
              disabled={isSubmitting}
              onClick={onCancelWithUnsavedConfirm}
            >
              Cancel
            </Button>
          </HStack>
        </FormStack>
      </Box>
      <NewDigitalMembershipCardPassConfigModal
        {...newPassConfigModal}
        refresh={refresh}
      />
      <MemberFilterQueryModal
        size="4xl"
        {...memberFilterQueryModal}
        onSuccess={onSuccessMemberFilterQuery}
        businessId={business.BusinessId!}
        memberQueryFilter={
          values.digitalMembershipConfiguration
            ?.ActiveMemberFilterQuerystring ?? ""
        }
      />
      {confirmationElement}
    </>
  );
};

export const EditBusinessForm = withFormik<
  EditBusinessFormProps,
  EditBusinessFormValues
>({
  mapPropsToValues: ({ business }) => {
    return {
      displayName: business?.DisplayName ?? "",
      enabled: business?.Enabled ?? true,
      licencedCherryHubProducts: business.LicencedCherryHubProducts,
      smsSenderId: business?.SmsSenderId ?? "",
      allowDigitalMembershipCardCreation:
        business?.AllowDigitalMembershipCardCreation,
      digitalMembershipCardPassConfigurationId:
        business?.DigitalMembershipCardPassConfigurationId ?? "",
      defaultApplePassTypeIdentifier:
        business?.DefaultApplePassTypeIdentifier ?? "",
      defaultApplePassTypeIdentifierForCoupons:
        business?.DefaultApplePassTypeIdentifierForCoupons ?? "",
      digitalMembershipConfiguration: business?.DigitalMembershipConfiguration
        ? {
            ...business.DigitalMembershipConfiguration,
            PassConfigurationRules: passConfigurationRulesMapToArray(
              business.DigitalMembershipConfiguration.PassConfigurationRules
            ),
            ExpireAllDmcAfterDate: DateUtil.convertToUtcDateTimeLocal(
              business.DigitalMembershipConfiguration.ExpireAllDmcAfterDate
            ),
            ObfuscateMemberValues:
              business.DigitalMembershipConfiguration.ObfuscateMemberValues ??
              false,
          }
        : {
            ExpireAllDmcAfterDate: "",
            ObfuscateMemberValues: false,
          },
      customerRiskManagementConfiguration:
        business?.CustomerRiskManagementConfiguration ?? null,
    };
  },

  handleSubmit: async (values, { props, setFieldError }) => {
    if (isEmptyStr(values.displayName)) {
      setFieldError("displayName", "Display name is required.");
      return;
    }

    const passConfigurationRulesToValidate =
      values.digitalMembershipConfiguration?.PassConfigurationRules ?? [];
    for (
      let index = 0;
      index < passConfigurationRulesToValidate.length;
      index++
    ) {
      const rule = passConfigurationRulesToValidate[index];
      if (isEmptyStr(rule.Description)) {
        setFieldError(
          `digitalMembershipConfiguration.PassConfigurationRules.${index}.Description`,
          "You must set a Description"
        );
        return;
      }
      if (isEmptyStr(rule.Test)) {
        setFieldError(
          `digitalMembershipConfiguration.PassConfigurationRules.${index}.Test`,
          "You must set a Test"
        );
        return;
      }
    }

    const normalizedPassConfigurationRules = passConfigurationRulesArrayToMap(
      values.digitalMembershipConfiguration?.PassConfigurationRules
    );

    // Translate form state to BusinessUpdate
    const businessDoc: CherryHubAdminApi.BusinessUpdate = {
      DisplayName: values.displayName,
      SmsSenderId: emptyStrToNull(values.smsSenderId),
      AllowDigitalMembershipCardCreation:
        values.allowDigitalMembershipCardCreation,
      LicencedCherryHubProducts: values.licencedCherryHubProducts,
      DigitalMembershipCardPassConfigurationId: emptyStrToNull(
        values.digitalMembershipCardPassConfigurationId
      ),
      DefaultApplePassTypeIdentifier: emptyStrToNull(
        values.defaultApplePassTypeIdentifier
      ),
      DefaultApplePassTypeIdentifierForCoupons: emptyStrToNull(
        values.defaultApplePassTypeIdentifierForCoupons
      ),
      Enabled: values.enabled,
      DigitalMembershipConfiguration: {
        ...values.digitalMembershipConfiguration,
        PassConfigurationRules: normalizedPassConfigurationRules,
        ExpireAllDmcAfterDate: DateUtil.convertFromUtcDateTimeLocal(
          values.digitalMembershipConfiguration?.ExpireAllDmcAfterDate
        ),
        ObfuscateMemberValues:
          values.digitalMembershipConfiguration?.ObfuscateMemberValues ?? false,
      },
      CustomerRiskManagementConfiguration:
        buildCustomerRiskManagementConfiguration(values),
    };

    let result = await props.apiClient.updateBusiness(
      props.business.BusinessId!,
      businessDoc
    );

    if (result.ok) {
      if (props.onSuccess) {
        props.onSuccess(result.data);
      }
    } else {
      if (props.onFailure) {
        props.onFailure(result.message);
      }
    }
  },

  validateOnBlur: false,
  validateOnMount: false,
  validateOnChange: true,

  validationSchema: Yup.object({
    smsSenderId: Yup.string().matches(/^[a-zA-Z0-9 ]{0,11}$/, {
      message:
        "Invalid SMS SenderId, only allow alphanumeric and space with maximum 11 characters",
      excludeEmptyString: false,
    }),
    digitalMembershipConfiguration: Yup.object({
      ExpireAllDmcAfterDate: Yup.string()
        .nullable()
        .test(
          "expire-all-dmc-not-in-past",
          "Expire All DMC After Date cannot be in the past.",
          (value) => !isExpireAllDmcAfterDateInPast(value)
        ),
    }).nullable(),
    customerRiskManagementConfiguration: Yup.mixed()
      .nullable()
      .test(
        "crm-config-validation",
        "Invalid Customer Risk Management configuration.",
        function (
          value:
            | CherryHubAdminApi.Business["CustomerRiskManagementConfiguration"]
            | null
            | undefined
        ) {
          if (value == null) {
            return true;
          }

          const bndryHasInput = hasBndryConfigInput(value);
          const au10HasInput = hasAu10TixConfigInput(value);

          if (
            bndryHasInput &&
            !hasValue(value.BndryApiClientConfiguration?.ClientId)
          ) {
            return this.createError({
              path: "customerRiskManagementConfiguration.BndryApiClientConfiguration.ClientId",
              message: "Client Id is required when Bndry configuration is set.",
            });
          }

          if (
            au10HasInput &&
            !hasValue(value.Au10TixApiClientConfiguration?.ClientId)
          ) {
            return this.createError({
              path: "customerRiskManagementConfiguration.Au10TixApiClientConfiguration.ClientId",
              message:
                "Client Id is required when Au10Tix configuration is set.",
            });
          }

          if (
            au10HasInput &&
            !hasValue(value.Au10TixApiClientConfiguration?.KeyId)
          ) {
            return this.createError({
              path: "customerRiskManagementConfiguration.Au10TixApiClientConfiguration.KeyId",
              message: "Key Id is required when Au10Tix configuration is set.",
            });
          }

          if (
            !isValidUrl(value.BndryApiClientConfiguration?.ApiBaseUrl ?? null)
          ) {
            return this.createError({
              path: "customerRiskManagementConfiguration.BndryApiClientConfiguration.ApiBaseUrl",
              message: "Bndry API Base Url must be a valid URL.",
            });
          }

          if (
            !isValidUrl(
              value.Au10TixApiClientConfiguration?.AuthApiBaseUrl ?? null
            )
          ) {
            return this.createError({
              path: "customerRiskManagementConfiguration.Au10TixApiClientConfiguration.AuthApiBaseUrl",
              message: "Au10Tix Auth API Base Url must be a valid URL.",
            });
          }

          return true;
        }
      ),
  }),
})(InnerForm);
